import app from 'flarum/forum/app';
import { extend } from 'flarum/common/extend';
import TextEditor from 'flarum/common/components/TextEditor';
import ReplyComposer from 'flarum/forum/components/ReplyComposer';
import DiscussionComposer from 'flarum/forum/components/DiscussionComposer';
import ComposerState from 'flarum/forum/states/ComposerState';
import Button from 'flarum/common/components/Button';
import GifPickerModal from './components/GifPickerModal';
import GifPickerButton from './components/GifPickerButton';

/**
 * Items picked but not yet submitted.
 *
 * Held on the ComposerState — the same object TextEditor receives as
 * `attrs.composer` and ReplyComposer reads as `this.composer` — rather than on
 * `app`, so clearing it is tied to the composer's own lifecycle below.
 */
function pending(composer) {
  if (!composer.happyGifyItems) {
    composer.happyGifyItems = [];
  }

  return composer.happyGifyItems;
}

/**
 * The text appended for a picked item.
 *
 * Markdown image syntax, not a bare URL. Checked against the local forum's
 * stored posts: a bare URL is parsed into `<URL>` and renders as a link, while
 * `![alt](url)` produces `<IMG src=...>` and actually embeds the GIF. Flarum
 * does not auto-embed image URLs on its own.
 *
 * The alt text is the item's own title, so the post stays meaningful for
 * screen readers and when the image cannot load. Brackets are stripped because
 * they would terminate the alt text early and break the markup.
 */
function textFor(item) {
  const alt = String(item.title || 'GIF').replace(/[[\]]/g, '');

  return `![${alt}](${item.url})`;
}

app.initializers.add('happydigital-gify', () => {
  // Adds the picker to the composer toolbar, alongside core's own formatting
  // controls, so it reads as part of the editor rather than a bolted-on extra.
  extend(TextEditor.prototype, 'toolbarItems', function (items) {
    if (!app.session.user) return;

    const composer = this.attrs.composer;

    const label = app.translator.trans('happydigital-gify.forum.button_tooltip');

    items.add(
      'happyGify',
      <GifPickerButton
        // GifPickerButton renders the SVG into the icon slot, so this keeps
        // core's default classes and matches the toolbar's other buttons.
        // Both are passed explicitly because the button has no text child for
        // TextEditorButton to extract.
        tooltipText={label}
        aria-label={label}
        onclick={() => {
          app.modal.show(GifPickerModal, {
            onselect: (gif) => {
              // Replace the array rather than pushing into it, so Mithril
              // reliably picks the change up on the next redraw.
              composer.happyGifyItems = [...pending(composer), gif];
              m.redraw();
            },
          });
        }}
      />
    );
  });

  // A strip of thumbnails under the editor, so a pick is visible and
  // removable instead of dropping a raw URL into the middle of the text.
  extend(TextEditor.prototype, 'view', function (vdom) {
    if (!vdom || !Array.isArray(vdom.children)) return;

    const composer = this.attrs.composer;
    const items = pending(composer);

    if (!items.length) return;

    vdom.children.push(
      <div className="HappyGify-previewStrip">
        {items.map((item, index) => (
          <div className="HappyGify-preview" key={item.id}>
            <img src={item.preview} alt={item.title} />
            <Button
              className="Button Button--icon HappyGify-previewRemove"
              icon="fas fa-times"
              aria-label={app.translator.trans('happydigital-gify.forum.remove')}
              onclick={(e) => {
                // The strip lives inside the composer's form; without this the
                // click would submit the post.
                e.preventDefault();

                composer.happyGifyItems = items.filter((_, i) => i !== index);
                m.redraw();
              }}
            />
          </div>
        ))}
      </div>
    );
  });

  // Picked items are appended when the post is submitted, so they land in the
  // post without ever cluttering the text the user is writing.
  function appendItems(data) {
    const items = this.composer.happyGifyItems;

    if (!items || !items.length) return;

    const text = items.map(textFor).join('\n\n');

    data.content = (data.content ? data.content + '\n\n' : '') + text;
  }

  extend(ReplyComposer.prototype, 'data', appendItems);
  extend(DiscussionComposer.prototype, 'data', appendItems);

  // ComposerState.clear() is the one place core resets a composer: it runs on
  // hide, after a successful post, and when a different composer is opened
  // over this one. Hooking it here means a pick can never leak into the next
  // post the way it would if we only cleared on close.
  extend(ComposerState.prototype, 'clear', function () {
    this.happyGifyItems = [];
  });
});
